import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';


const Dropdown = ({ 
    children, 
    overlay, 
    trigger = ['click'], 
    placement = 'bottomRight',
    onOpenChange,
    disabled = false,
    className = '',
    overlayClassName = '',
    mouseEnterDelay = 100,
    mouseLeaveDelay = 100
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const triggerRef = useRef(null);
    const hoverTimeoutRef = useRef(null);

    const shouldTriggerOnClick = trigger.includes('click');
    const shouldTriggerOnHover = trigger.includes('hover');

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
                triggerRef.current && !triggerRef.current.contains(event.target)) {
                handleClose();
            }
        };

        const handleEscapeKey = (event) => {
            if (event.key === 'Escape' && isOpen) {
                handleClose();
            }
        };

        if (isOpen && shouldTriggerOnClick) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscapeKey);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isOpen, shouldTriggerOnClick]);

    const handleOpen = () => {
        if (disabled) return;
        setIsOpen(true);
        if (onOpenChange) onOpenChange(true);
    };

    const handleClose = () => {
        setIsOpen(false);
        if (onOpenChange) onOpenChange(false);
    };

    const handleToggle = () => {
        if (disabled) return;
        const newState = !isOpen;
        setIsOpen(newState);
        if (onOpenChange) onOpenChange(newState);
    };

    const handleMouseEnter = () => {
        if (!shouldTriggerOnHover || disabled) return;
        
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        
        hoverTimeoutRef.current = setTimeout(() => {
            handleOpen();
        }, mouseEnterDelay);
    };

    const handleMouseLeave = () => {
        if (!shouldTriggerOnHover) return;
        
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        
        hoverTimeoutRef.current = setTimeout(() => {
            handleClose();
        }, mouseLeaveDelay);
    };
  const { t, isRTL } = useLanguage();

    const getPositionClasses = () => {
        switch (placement) {
            case 'bottomLeft':
                return 'top-full right-2 md:right-12 mt-2 md:mt-0';
            case 'bottomRight':
                return 'top-full left-2 md:right-7 mt-3 md:mt-0';
            case 'bottom':
                return 'top-full left-1/2 transform -translate-x-1/2 mt-2';
            case 'topLeft':
                return 'bottom-full left-0 mb-2 ';
            case 'topRight':
                return 'bottom-full right-10 mb-2';
            case 'top':
                return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2';
            case "notDrop":
                return isRTL?'top-full left-[20%] md:left-[10%] mt-3':'top-full right-[20%] md:right-1 mt-3';
            case "unAuthDrop":
                return isRTL?'top-full left-[20%] md:left-[10%] mt-3':'top-full right-[20%] md:right-1 mt-3';
            default:
                return 'top-full right-0 mt-2';
        }
    };

    const triggerProps = {
        ref: triggerRef,
        className: `${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} ${className}`,
        ...(shouldTriggerOnClick && { onClick: handleToggle }),
        ...(shouldTriggerOnHover && { 
            onMouseEnter: handleMouseEnter, 
            onMouseLeave: handleMouseLeave 
        })
    };

    return (
        <div className="relative inline-block">
            <div {...triggerProps}>
                {children}
            </div>
            {isOpen && (
                <div 
                    ref={dropdownRef}
                    className={`absolute z-50 ${getPositionClasses()} ${overlayClassName}`}
                    {...(shouldTriggerOnHover && { 
                        onMouseEnter: handleMouseEnter, 
                        onMouseLeave: handleMouseLeave 
                    })}
                >
                    {overlay}
                </div>
            )}
        </div>
    );
};

export default Dropdown;